import { skipToken, useApolloClient, useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  FieldEventsType,
  ReferenceValueOptionsType,
} from "@/components/@types";
import { List } from "@/components/organisms/List";
import { useListContext } from "@/components/organisms/List/List.hooks";
import { useNodeListQuery } from "@/components/organisms/Node/Node.hooks";
import { cn } from "@/components/ui/utils";

/**
 * Convenience wrapper around ``<List>`` that fetches reference rows
 * via ``useNodeListQuery``. Suits the toolbar-style "show me a list of
 * objects from a single GraphQL query" case. Rows a second query finds for
 * what is typed (``searchOptions``) follow them as rows of the same list.
 *
 * For pickers that need to mix live references with other row sources
 * (e.g. the workflow filter value picker, which interleaves upstream
 * step fields with live references and wants a single search +
 * keyboard-navigation pipeline), reach for ``useNodeListQuery`` and
 * compose your own ``<List>`` directly instead.
 */
export function NodeList<T>({
  filterOptions = null,
  getIsChecked = () => false,
  isMultiselect = false,
  isCheckboxesEnabled,
  isNullable = true,
  maxCheckedRows,
  mutationOptions = null,
  onRenderOption = () => <></>,
  onSelectMultiSelectOption = null,
  onSelectOption = () => null,
  onSubmitCheckedRows = () => null,
  onActiveIndexChange,
  prepareOptions,
  resolveSelection,
  getOptionGroup,
  searchOptions,
  query,
  queryOptions = null,
  size = "md",
  className,
  autofocus = false,
  fillHeight = true,
  events,
  currentValues,
}: {
  filterOptions?: (options: T[], filter: string) => T[];
  getIsChecked?: (option: T) => boolean;
  isMultiselect?: boolean;
  isCheckboxesEnabled?: boolean;
  isNullable?: boolean;
  maxCheckedRows?: number;
  onCreateNewOption?: (filter: string) => void | Promise<unknown>;
  onSelectMultiSelectOption?: (option: T) => void;
  onSelectOption?: (option: T, isChecked?: boolean) => void;
  onSubmitCheckedRows?: (
    checkedRows: T[],
    uncheckedRows: T[],
    selectedRow: T,
  ) => void;
  onActiveIndexChange?: (data: T, onClick: () => void) => void;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  autofocus?: boolean;
  fillHeight?: boolean;
  // Forwarded by ``FieldReferencePopper`` so the inline "create new"
  // handler can hand the freshly-created row straight back to the
  // field's submission pipeline.
  events?: FieldEventsType;
  currentValues?: T[];
} & ReferenceValueOptionsType<T>) {
  const useCheckboxes = isCheckboxesEnabled ?? isMultiselect;
  const apolloClient = useApolloClient();

  const { options, isLoading } = useNodeListQuery<T>({
    query,
    queryOptions,
    prepareOptions,
  });

  // What is typed into the search once typing pauses, for ``searchOptions``
  // to ask for: its rows follow the options, its placeholder while it asks.
  const [searched, setSearched] = useState("");
  const searchVariables = searchOptions?.variables(searched) ?? null;
  const { data: searchData, loading: isSearching } = useQuery(
    // Without ``searchOptions`` the list's own query stands in, never asked.
    searchOptions?.query ?? query,
    searchVariables ? { variables: searchVariables } : skipToken,
  );
  const isSearched = searchVariables !== null;
  const rows = useMemo(
    () =>
      isSearched
        ? [
            ...options,
            ...(isSearching
              ? [searchOptions.placeholder]
              : searchOptions.prepareOptions(searchData)),
          ]
        : options,
    [options, isSearched, isSearching, searchData, searchOptions],
  );

  const onRowClick = useCallback(async (option: T, isChecked?: boolean) => {
    const chosen = resolveSelection
      ? await resolveSelection(option, { apolloClient })
      : option;
    if (chosen) onSelectOption(chosen, isChecked);
  }, []);
  const onRenderRow = useCallback(
    (item: T, index: number) =>
      onRenderOption(item, index, {
        select: (option: T) => onSelectOption(option),
        apolloClient,
      }),
    [],
  );

  const getRowKey = useCallback(
    (option: T) => (option as { identifier?: string | number }).identifier!,
    [],
  );

  // Wrap the user-supplied create handler so the helpers contract is
  // honoured regardless of whether the picker host remembered to pass
  // ``events`` / ``currentValues`` down. The List subtree only knows
  // about ``onNewRow(filter)`` so this wrapper is the integration
  // point between ``ReferenceValueOptionsType.mutationOptions`` and
  // the picker's submission pipeline.
  const handleCreateNew = useCallback(
    mutationOptions?.onCreateNewOption
      ? (filter: string) =>
          mutationOptions.onCreateNewOption!(filter, {
            events,
            currentValues: currentValues ?? [],
            apolloClient,
          })
      : (undefined as unknown as (filter: string) => void),
    [mutationOptions?.onCreateNewOption, events, currentValues, apolloClient],
  );

  return (
    <List<T>
      data={rows}
      filterOptions={filterOptions}
      getIsChecked={getIsChecked}
      getRowKey={getRowKey}
      getShouldSkipRow={
        searchOptions
          ? (row: T) => row === searchOptions.placeholder
          : undefined
      }
      isMultiselect={isMultiselect}
      isCheckboxesEnabled={useCheckboxes}
      isNullable={isNullable}
      maxCheckedRows={maxCheckedRows}
      isLoading={isLoading}
      onItemClick={onRowClick}
      onSelectMultiSelectOption={onSelectMultiSelectOption}
      onSubmitCheckedRows={onSubmitCheckedRows}
      onNewRow={
        mutationOptions?.onCreateNewOption ? handleCreateNew : undefined
      }
      onActiveIndexChange={onActiveIndexChange}
      size={size as "sm" | "md" | "lg"}
      fillHeight={fillHeight}
      // No fixed ``maxHeight`` here — the parent (Popper / embed
      // wrapper) owns height bounding. Callers that mount NodeList
      // outside a flex-bounded parent (e.g. a bounded grid cell)
      // can still pass a ``max-h-*`` class to opt back into a fixed
      // ceiling; their ``className`` is merged last.
      className={cn(
        fillHeight === false
          ? "min-w-[calc(340px)] max-w-[calc(640px)] flex-[0_1_auto] min-h-0 flex flex-col relative rounded-sm"
          : fillHeight === true
            ? "min-w-[calc(340px)] max-w-[calc(640px)] flex-1 min-h-0 flex flex-col relative rounded-sm"
            : "min-w-[calc(340px)] max-w-[calc(640px)] flex-1 min-h-0 flex flex-col relative rounded-sm",
        className,
      )}
      autofocus={autofocus}
    >
      {filterOptions && <List.SearchInput />}
      {isMultiselect && !useCheckboxes && (
        <List.CheckedItems>
          {(item: T) => onRenderRow(item, 0)}
        </List.CheckedItems>
      )}
      <List.Items<T> getGroup={getOptionGroup}>
        {(item: T) => (
          <List.Item
            value={item}
            data-testid={`list-option-${(((item as { name?: string }).name ?? (item as { identifier?: string }).identifier ?? "") + "").toLowerCase().replace(/\s/g, "-")}`}
          >
            {onRenderRow(item, 0)}
          </List.Item>
        )}
      </List.Items>
      {searchOptions && <NodeListSearchPause onPause={setSearched} />}
      {mutationOptions?.onCreateNewOption && (
        <List.CreateNewFooter label={mutationOptions.createNewLabel} />
      )}
      <List.LoadingState />
      {isMultiselect && <List.MultiSelectFooter />}
    </List>
  );
}

/**
 * Hands ``onPause`` what is typed into the list's search once typing pauses:
 * the list keeps it to itself, and a picker's ``searchOptions`` asks for it.
 */
function NodeListSearchPause({
  onPause,
}: {
  onPause: (filter: string) => void;
}) {
  const { filter } = useListContext();
  useEffect(() => {
    const timeout = setTimeout(() => onPause(filter.trim()), 250);
    return () => clearTimeout(timeout);
  }, [filter, onPause]);
  return null;
}
